import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { thumb, preview } from "@/lib/media";
import { PubDialog } from "./Fichas";

const PAGE = 24;
const AUTO_PAGES = 4;
const FILTROS = [["", "Todos"], ["foto", "Fotos"], ["video", "Videos"]];

export const PlayIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" aria-hidden="true"><path d="M7 4.5v13l11-6.5z" fill="#3A3939" /></svg>
);

export const MediaViewer = ({ items, index, onIndex, onClose }) => {
  const m = items[index];
  const go = useCallback((d) => onIndex((index + d + items.length) % items.length), [index, items.length, onIndex]);
  useEffect(() => {
    const k = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go]);
  if (!m) return null;
  return (
    <PubDialog onClose={onClose} testid="media-dialog" wide>
      <div className="mview" data-testid={`media-view-${m.tipo}`}>
        {m.tipo === "video" ? (
          <div className="vframe">
            <iframe key={m.id} src={preview(m.id)} title="Video Once y Once" allow="autoplay; fullscreen" allowFullScreen loading="lazy" data-testid="media-video-iframe" />
          </div>
        ) : (
          <img key={m.id} src={thumb(m.id, 1600)} alt="Foto de la Fundación Once y Once" referrerPolicy="no-referrer" data-testid="media-photo-large" />
        )}
      </div>
      {items.length > 1 && (
        <div className="mnav">
          <button className="btn xbtn" onClick={() => go(-1)} data-testid="media-prev-btn">← Anterior</button>
          <span className="note">{index + 1} de {items.length}</span>
          <button className="btn xbtn" onClick={() => go(1)} data-testid="media-next-btn">Siguiente →</button>
        </div>
      )}
    </PubDialog>
  );
};

function useGallery(tipo) {
  const [items, setItems] = useState([]);
  const [info, setInfo] = useState({ total: null, fotos: 0, videos: 0 });
  const st = useRef({ busy: false, n: 0, total: Infinity, gen: 0 });
  const more = useCallback(async () => {
    const s = st.current;
    if (s.busy || s.n >= s.total) return;
    s.busy = true;
    try {
      const { data } = await api.get("/media", { params: { tipo, skip: s.n, limit: PAGE } });
      if (s !== st.current) return;
      s.n += data.items.length;
      s.total = data.total;
      setItems((p) => [...p, ...data.items]);
      setInfo(data);
    } catch (e) {
      s.total = s.n;
    } finally {
      s.busy = false;
    }
  }, [tipo]);
  useEffect(() => {
    st.current = { busy: false, n: 0, total: Infinity, gen: st.current.gen + 1 };
    setItems([]);
    more();
  }, [more]);
  return { items, info, more };
}

const Tile = ({ m, onOpen }) => (
  <button className="gitem" onClick={onOpen} aria-label={m.tipo === "video" ? "Ver video" : "Ver foto"} data-testid={`galeria-item-${m.id}`}>
    <img src={thumb(m.id, 600)} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.opacity = 0; }} />
    {m.tipo === "video" && <span className="cplay gplay"><PlayIcon /></span>}
  </button>
);

export const Galeria = () => {
  const [tipo, setTipo] = useState("");
  const [open, setOpen] = useState(null);
  const { items, info, more } = useGallery(tipo);
  const sentinel = useRef(null);
  const auto = items.length < PAGE * AUTO_PAGES;
  const hayMas = info.total !== null && items.length < info.total;

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !auto || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver((es) => { if (es[0].isIntersecting) more(); }, { rootMargin: "500px" });
    io.observe(el);
    return () => io.disconnect();
  }, [more, auto, items.length]);

  return (
    <section id="galeria" data-testid="galeria-section">
      <div className="wrap">
        <div className="head reveal in">
          <div>
            <h2>Galería Once y Once</h2>
            <p className="lead" style={{ margin: "10px 0 0" }}>Entrenamientos, talleres y encuentros con nuestros deportistas y artistas.</p>
          </div>
          <div className="chips" role="group" aria-label="Filtrar galería" style={{ margin: 0 }}>
            {FILTROS.map(([k, l]) => (
              <button key={k || "todos"} className="chip" aria-pressed={tipo === k} onClick={() => { setTipo(k); setOpen(null); }} data-testid={`galeria-filtro-${k || "todos"}`}>
                {l}{k === "foto" && info.fotos ? ` · ${info.fotos}` : ""}{k === "video" && info.videos ? ` · ${info.videos}` : ""}
              </button>
            ))}
          </div>
        </div>
        <div className="ggrid" data-testid="galeria-grid">
          {items.map((m, i) => <Tile key={m.id} m={m} onOpen={() => setOpen(i)} />)}
        </div>
        {info.total === 0 && <p className="note" data-testid="galeria-vacia">Pronto publicaremos nuevas fotos y videos.</p>}
        <div ref={sentinel} aria-hidden="true" style={{ height: 1 }} />
        {hayMas && !auto && (
          <div style={{ textAlign: "center", marginTop: 22 }}>
            <button className="btn btn-sol" onClick={more} data-testid="galeria-ver-mas-btn">Ver 24 más</button>
          </div>
        )}
        <p className="note gaviso" data-testid="galeria-aviso">
          Imágenes publicadas con autorización. Si deseas retirar una imagen <a href="#contacto" data-testid="galeria-aviso-contacto-link">escríbenos</a>.
        </p>
      </div>
      {open !== null && <MediaViewer items={items} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />}
    </section>
  );
};
